<template>
  <div
    v-click-outside="closeOnBlur ? closePopover : () => {}"
    v-bind="$attrs"
    class="base-popover"
  >
    <slot
      :open-popover="openPopover"
      :close-popover="closePopover"
      :toggle-popover="togglePopover"
      :is-visible="popoverIsVisible"
      name="trigger"
    />

    <div 
      :class="basePopoverControlClasses" 
      class="base-popover__control"
    >
      <transition name="scale">
        <base-card
          v-if="popoverIsVisible"
          :style="{ 'max-height': maxHeight }"
          :class="basePopoverCardClasses"
          class="base-popover__card"
        >
          <slot
            :close-popover="closePopover"
            name="popover"
          />
        </base-card>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  closeOnBlur: {
    type: Boolean,
    default: true,
  },
  alignment: {
    type: String,
    default: "bottom left",
    validator: function (value) {
      return [
        "",
        "top left",
        "top center",
        "top right",
        "right top",
        "right center",
        "right bottom",
        "bottom right",
        "bottom center",
        "bottom left",
        "left bottom",
        "left center",
        "left top",
        "custom",
        "auto",
      ].includes(value);
    },
  },
  maxHeight: {
    type: String,
    default: "320px",
  },
  padding: {
    type: String,
    default: "none",
    validator: function (value) {
      return [
        "",
        "none",
        "x-tight",
        "tight",
        "base-tight",
        "base",
        "loose",
        "x-loose",
      ].includes(value);
    },
  },
});

const emit = defineEmits(["update:isVisible"]);

const popoverIsVisible = ref(props.isVisible);

// Keep local ref in sync with prop changes
watch(
  () => props.isVisible,
  (newValue) => {
    popoverIsVisible.value = newValue;
  },
  { immediate: true }
);

// Emit updates for two-way binding
watch(popoverIsVisible, (newValue) => {
  emit("update:isVisible", newValue);
});

const basePopoverControlClasses = computed(() => ({
  "base-popover__control--align-top-left": props.alignment === "top left",
  "base-popover__control--align-top-center":
    props.alignment === "top center",
  "base-popover__control--align-top-right":
    props.alignment === "top right",
  "base-popover__control--align-right-top":
    props.alignment === "right top",
  "base-popover__control--align-right-center":
    props.alignment === "right center",
  "base-popover__control--align-right-bottom":
    props.alignment === "right bottom",
  "base-popover__control--align-bottom-left":
    props.alignment === "bottom left" || !props.alignment,
  "base-popover__control--align-bottom-center":
    props.alignment === "bottom center",
  "base-popover__control--align-bottom-right":
    props.alignment === "bottom right",
  "base-popover__control--align-left-top": props.alignment === "left top",
  "base-popover__control--align-left-center":
    props.alignment === "left center",
  "base-popover__control--align-left-bottom":
    props.alignment === "left bottom",
}));

const basePopoverCardClasses = computed(() => ({
  "base-popover__container--padding-none":
    props.padding === "none" || !props.padding,
  "base-popover__container--padding-x-tight": props.padding === "x-tight",
  "base-popover__container--padding-tight": props.padding === "tight",
  "base-popover__container--padding-base-tight":
    props.padding === "base-tight",
  "base-popover__container--padding-base": props.padding === "base",
  "base-popover__container--padding-loose": props.padding === "loose",
  "base-popover__container--padding-x-loose": props.padding === "x-loose",
}));

const openPopover = () => {
  popoverIsVisible.value = true;
};

const closePopover = () => {
  popoverIsVisible.value = false;
};

const togglePopover = () => {
  popoverIsVisible.value = !popoverIsVisible.value;
};
</script>

<style lang="scss" scoped>
@import "./base-popover.scss";
</style>
